import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, ArrowLeft, Loader2 } from "lucide-react";
import RichText from "@/components/common/RichText";
import ImageUpload from "@/components/common/ImageUpload";

const empty = {
  car_slug: "", title: "", slug: "", hero_image: "",
  expert_rating: 7.5, verdict: "", pros: [], cons: [], body: "",
  images: [], video_url: "", author: "TrendingNewCar Editorial",
  status: "draft",
  meta_title: "", meta_description: "", canonical_url: "", og_image: "",
};

export default function ReviewsAdmin() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // full review object
  const [busy, setBusy] = useState(false);

  const load = () => {
    setBusy(true);
    api.get("/admin/reviews").then((r) => setRows(r.data)).finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    if (!editing.slug || !editing.title || !editing.car_slug) {
      toast.error("slug, title and car_slug are required");
      return;
    }
    try {
      const isNew = !rows.find((r) => r.slug === editing.slug) && !editing._existing;
      if (isNew) {
        await api.post("/admin/reviews", editing);
      } else {
        await api.put(`/admin/reviews/${editing.slug}`, editing);
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (slug) => {
    if (!window.confirm(`Delete review "${slug}"?`)) return;
    await api.delete(`/admin/reviews/${slug}`);
    load();
  };

  if (editing) {
    return (
      <div data-testid="admin-reviews-edit">
        <button onClick={() => setEditing(null)} className="text-sm text-blue-600 inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4"/> Back to reviews
        </button>
        <h1 className="font-display text-3xl tracking-tighter">
          {editing._existing ? "Edit review" : "New review"}
        </h1>

        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Field label="Title *"><input data-testid="review-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inp}/></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Slug *"><input data-testid="review-slug" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value.replace(/\s+/g, "-").toLowerCase() })} className={inp}/></Field>
              <Field label="Car slug *"><input data-testid="review-car-slug" value={editing.car_slug} onChange={(e) => setEditing({ ...editing, car_slug: e.target.value })} className={inp}/></Field>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Expert rating (0-10)"><input type="number" step="0.1" min="0" max="10" data-testid="review-rating" value={editing.expert_rating} onChange={(e) => setEditing({ ...editing, expert_rating: parseFloat(e.target.value) })} className={inp}/></Field>
              <Field label="Author"><input value={editing.author || ""} onChange={(e) => setEditing({ ...editing, author: e.target.value })} className={inp}/></Field>
              <Field label="Status">
                <select data-testid="review-status" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className={inp}>
                  <option value="draft">Draft</option><option value="published">Published</option>
                </select>
              </Field>
            </div>
            <Field label="Verdict"><input data-testid="review-verdict" value={editing.verdict} onChange={(e) => setEditing({ ...editing, verdict: e.target.value })} className={inp}/></Field>
            <ListEditor label="Pros" testId="review-pros" values={editing.pros} onChange={(v) => setEditing({ ...editing, pros: v })}/>
            <ListEditor label="Cons" testId="review-cons" values={editing.cons} onChange={(v) => setEditing({ ...editing, cons: v })}/>
            <Field label="Body">
              <RichText testId="review-body" value={editing.body} onChange={(html) => setEditing({ ...editing, body: html })}/>
            </Field>
            <Field label="Video URL (optional)"><input value={editing.video_url || ""} onChange={(e) => setEditing({ ...editing, video_url: e.target.value })} className={inp}/></Field>
          </div>

          <div className="space-y-4">
            <Field label="Hero image">
              <ImageUpload value={editing.hero_image} onChange={(v) => setEditing({ ...editing, hero_image: v })} folder="reviews" testId="review-hero"/>
            </Field>
            <div className="rounded-3xl border border-[#E5E7EB] bg-white p-4 space-y-3">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">SEO</div>
              <Field label="Meta title"><input value={editing.meta_title || ""} onChange={(e) => setEditing({ ...editing, meta_title: e.target.value })} className={inp}/></Field>
              <Field label="Meta description"><textarea value={editing.meta_description || ""} onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} className={`${inp} min-h-[80px]`}/></Field>
              <Field label="Canonical URL"><input value={editing.canonical_url || ""} onChange={(e) => setEditing({ ...editing, canonical_url: e.target.value })} className={inp}/></Field>
              <Field label="OG image URL"><input value={editing.og_image || ""} onChange={(e) => setEditing({ ...editing, og_image: e.target.value })} className={inp}/></Field>
            </div>
            <button onClick={save} data-testid="review-save" className="btn-primary w-full h-11 text-sm inline-flex items-center justify-center gap-2">
              <Save className="w-4 h-4"/> Save review
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-reviews">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-tighter">Reviews</h1>
          <p className="text-sm text-muted-foreground mt-1">Expert reviews with pros/cons, verdict, rich body and SEO.</p>
        </div>
        <button data-testid="review-new" onClick={() => setEditing({ ...empty })} className="btn-primary h-10 px-4 text-sm inline-flex items-center gap-2">
          <Plus className="w-4 h-4"/> New review
        </button>
      </div>
      <div className="mt-6 rounded-3xl border border-[#E5E7EB] overflow-hidden bg-white">
        {busy ? (
          <div className="grid place-items-center py-16 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin"/></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground text-sm">No reviews yet. Create your first one.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC]"><tr>
              <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Title</th>
              <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Car</th>
              <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Rating</th>
              <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Status</th>
              <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Updated</th>
              <th className="p-3"/>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.slug} data-testid={`review-row-${r.slug}`} className="border-t border-[#E5E7EB]">
                  <td className="p-3 font-medium">{r.title}</td>
                  <td className="p-3 text-muted-foreground">{r.car_slug}</td>
                  <td className="p-3 font-mono">{r.expert_rating?.toFixed?.(1) ?? r.expert_rating}</td>
                  <td className="p-3">{r.status}</td>
                  <td className="p-3 text-xs text-muted-foreground">{r.updated_at?.slice(0, 10)}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => setEditing({ ...r, _existing: true })} data-testid={`review-edit-${r.slug}`} className="text-sm text-blue-600 hover:underline mr-3">Edit</button>
                    <button onClick={() => del(r.slug)} data-testid={`review-del-${r.slug}`} className="text-sm text-rose-600 hover:underline">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const inp = "w-full h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm outline-none focus:border-[#2563EB]";
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function ListEditor({ label, values, onChange, testId }) {
  const [text, setText] = useState("");
  return (
    <Field label={label}>
      <div className="space-y-1.5">
        {(values || []).map((v, i) => (
          <div key={`${v}-${i}`} className="flex items-center gap-2">
            <div className="flex-1 h-9 px-3 rounded-lg border border-[#E5E7EB] bg-[#F8FAFC] text-sm">{v}</div>
            <button type="button" onClick={() => onChange(values.filter((_, ix) => ix !== i))}
              className="h-9 w-9 rounded-lg border border-[#E5E7EB] bg-white text-rose-600 grid place-items-center">
              <Trash2 className="w-3 h-3"/>
            </button>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <input data-testid={`${testId}-input`} value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (text.trim()) { onChange([...(values || []), text.trim()]); setText(""); } } }}
            placeholder={`Add a ${label.toLowerCase()} point and press Enter`}
            className="flex-1 h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm outline-none focus:border-[#2563EB]"/>
        </div>
      </div>
    </Field>
  );
}
